import { useState } from "react";
import { Search, Calendar, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import { SkillsFilter } from "@/components/candidates/search/SkillsFilter";
import { JobsStatusFilter } from "@/components/jobs/JobsStatusFilter";
import { FavoriteFilter } from "@/components/common/FavoriteFilter";
import { FilterBadges } from "@/components/candidates/search/FilterBadges";
import { useTranslation } from "react-i18next";

interface JobsSearchProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  skillsFilter: string[];
  setSkillsFilter: (skills: string[]) => void;
  skillsOperator: "AND" | "OR";
  setSkillsOperator: (operator: "AND" | "OR") => void;
  statusFilter: string[];
  setStatusFilter: (status: string[]) => void;
  showClosed: boolean;
  setShowClosed: (show: boolean) => void;
  maxDaysAgo: number | null;
  setMaxDaysAgo: (days: number | null) => void;
  showFavoritesOnly: boolean;
  setShowFavoritesOnly: (show: boolean) => void;
  allSkills: string[];
  allStatuses: string[];
  viewMode: "card" | "list" | "kanban";
}

export const JobsSearch = ({ 
  searchTerm, 
  setSearchTerm,
  skillsFilter,
  setSkillsFilter,
  skillsOperator,
  setSkillsOperator,
  statusFilter,
  setStatusFilter,
  showClosed,
  setShowClosed,
  maxDaysAgo,
  setMaxDaysAgo,
  showFavoritesOnly,
  setShowFavoritesOnly,
  allSkills,
  allStatuses,
  viewMode
}: JobsSearchProps) => {
  const { t } = useTranslation();
  const [popoverOpen, setPopoverOpen] = useState(false);
  
  // Toggle skill selection
  const toggleSkill = (skill: string) => {
    if (skillsFilter.includes(skill)) {
      setSkillsFilter(skillsFilter.filter(s => s !== skill));
    } else {
      setSkillsFilter([...skillsFilter, skill]);
    }
  };
  
  // Toggle status selection
  const toggleStatus = (status: string) => {
    if (statusFilter.includes(status)) {
      setStatusFilter(statusFilter.filter(s => s !== status));
    } else {
      setStatusFilter([...statusFilter, status]);
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSkillsFilter([]);
    setSkillsOperator("AND");
    setStatusFilter([]);
    setShowClosed(false);
    setMaxDaysAgo(60);
    setSearchTerm("");
  };

  return (
    <div className="mb-6">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input 
            type="text"
            placeholder={t("jobs.searchPlaceholder", "Search jobs...")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-10"
          />
          {searchTerm && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
              onClick={() => setSearchTerm('')}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">{t("common.clearSearch", "Clear search")}</span>
            </Button>
          )}
        </div>
        
        <div className="flex gap-2">
          <SkillsFilter 
            skillsFilter={skillsFilter} 
            setSkillsFilter={setSkillsFilter}
            skillsOperator={skillsOperator}
            setSkillsOperator={setSkillsOperator}
            allSkills={allSkills} 
          />
          
          <JobsStatusFilter 
            statusFilter={statusFilter} 
            setStatusFilter={setStatusFilter} 
          />
          
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline">
                <Calendar className="h-4 w-4 mr-1" />
                {maxDaysAgo === null
                  ? t("jobs.filterPopover.allJobs", "All jobs")
                  : t("jobs.filterPopover.lastXDays", `Last ${maxDaysAgo} days`, { count: maxDaysAgo })}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80">
              <div className="mb-4 flex items-center justify-between">
                <span className="font-medium text-sm">
                  {maxDaysAgo === null
                    ? t("jobs.filterPopover.showAllJobs", "Show all jobs")
                    : t("jobs.filterPopover.showJobsFromLastXDays", `Show jobs from last ${maxDaysAgo} days`, { count: maxDaysAgo })}
                </span>
                <Button variant="ghost" size="sm" onClick={() => { setMaxDaysAgo(null); setPopoverOpen(false); }}>
                  {t("jobs.filterPopover.showAll", "Show all")}
                </Button>
              </div>
              <div 
                onDoubleClick={() => setMaxDaysAgo(60)}
                className="cursor-pointer"
                title="Double-click to reset to 60 days"
              >
                <Slider
                  min={1}
                  max={365}
                  step={1}
                  value={[maxDaysAgo || 60]}
                  onValueChange={([val]) => setMaxDaysAgo(val)}
                />
              </div>
              <div className="flex justify-between text-xs mt-2 text-gray-500">
                <span>{t("jobs.filterPopover.min", "1")}</span>
                <span>{t("jobs.filterPopover.max", "365")}</span>
              </div>
            </PopoverContent>
          </Popover>
          
          {/* Hide "Show Closed" button in kanban view since closed jobs are always visible */}
          {viewMode !== 'kanban' && (
            <label className="flex items-center gap-2 cursor-pointer h-9 px-4 py-2 border border-input rounded-sm hover:bg-accent hover:text-accent-foreground">
              <Checkbox
                id="show-closed"
                checked={showClosed}
                onCheckedChange={(checked) => setShowClosed(checked as boolean)}
                className="h-4 w-4"
              />
              <span className="text-sm">
                {t("jobs.showClosed", "Show Closed")}
              </span>
            </label>
          )}
          
          <FavoriteFilter
            showFavoritesOnly={showFavoritesOnly}
            setShowFavoritesOnly={setShowFavoritesOnly}
          />
          
        </div>
      </div>
      
      <FilterBadges
        skillsFilter={skillsFilter}
        skillsOperator={skillsOperator}
        toggleSkill={toggleSkill}
        statusFilter={statusFilter}
        toggleStatus={toggleStatus}
        functionFilter=""
        setFunctionFilter={() => {}}
        clearAllFilters={clearAllFilters}
      />
    </div>
  );
}; 